import { Column, Row } from 'further-ui/components/Layout';
import { FC } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { TrancheFormInput } from '../../useTrancheForm';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import TooltipTypography from 'further-ui/components/TooltipTypography';
import Table from 'further-ui/components/Table';
import { Column as TableColumn } from 'further-ui/components/Table/types';
import { styled } from '@mui/material/styles';
import { CurrencyCode } from 'further-types/lp/vehicle';
import api from 'lib/trpcClient';
import { SortOrder } from 'further-types/utils';
import {
  FeeAccounting,
  FeeCalculationBasis,
  FeeHurdleBasis,
  FeeType,
} from 'further-types/investment';
import {
  CoreFeeTypeLabels,
  FeeCalculationBasisLabels,
  FeeChargingFrequencyLabels,
} from 'further-ui/labels';
import {
  numberToCurrencyString,
  numberToPercentageString,
  getCurrencySymbol,
  renderMultiple,
} from 'further-ui/utils';
import lookupDisplayFeeType from 'utils/lookup-display-fee-type';
import {
  CORE_FEE_TEMPLATE_ADD,
  PERFORMANCE_FEE_TEMPLATE_ADD,
} from 'adminConstants/routes';
import { InfoOutline } from '@mui/icons-material';
import { Typography } from '@mui/material';
import TypographyButton from 'further-ui/components/TypographyButton';

const AdHocTooltipText = styled(TooltipTypography)({
  alignSelf: 'flex-start',
});

type CoreFeeRow = {
  id: string;
  feeName: string;
  feeType: FeeType;
  feeCalculationBasis: FeeCalculationBasis;
  netFee: number;
  grossFee: number;
  vat: number;
  chargingFrequency?: string | null;
  startingPeriod?: number | null;
  finishingPeriod?: number | null;
  feeAccounting: FeeAccounting;
};

type PerformanceFeeRow = {
  id: string;
  feeName: string;
  hurdle: number;
  hurdleBasis: FeeHurdleBasis;
  includeFeesInHurdle: boolean;
  feePercentageIncVAT: number;
  feePercentageExVAT: number;
  vatPercentage: number;
};

const buildCoreFeeColumns = (
  currency: CurrencyCode,
): Array<TableColumn<CoreFeeRow>> => [
  { label: 'Fee name', key: 'feeName', width: 200 },
  {
    label: 'Fee type',
    key: 'feeType',
    width: 130,
    render: (row) =>
      CoreFeeTypeLabels[
        lookupDisplayFeeType(row.feeType, row.feeCalculationBasis)
      ],
  },
  {
    label: 'Charging frequency',
    key: 'chargingFrequency',
    width: 150,
    render: (row) => FeeChargingFrequencyLabels[row.chargingFrequency ?? ''],
  },
  {
    label: 'Fee basis',
    key: 'feeCalculationBasis',
    width: 170,
    render: (row) => FeeCalculationBasisLabels[row.feeCalculationBasis ?? ''],
  },
  { label: 'Start period', key: 'startingPeriod', width: 100 },
  { label: 'Finish period', key: 'finishingPeriod', width: 100 },
  {
    label: `Fee %/${getCurrencySymbol(currency)} (ex VAT)`,
    key: 'netFee',
    width: 150,
    render: (row) =>
      row.feeCalculationBasis ===
        FeeCalculationBasis.PercentageOfTotalCommitment ||
      row.feeCalculationBasis ===
        FeeCalculationBasis.PercentageOfUnrealisedAssetCost
        ? numberToPercentageString(row.netFee, {
            accuracy: 2,
            forceDecimals: true,
            noMultiply: true,
          })
        : numberToCurrencyString(row.netFee, { currency }),
  },
  {
    label: 'VAT',
    key: 'vat',
    width: 100,
    render: (row) =>
      row.vat === 0
        ? 'No VAT'
        : numberToPercentageString(row.vat, {
            accuracy: 2,
            forceDecimals: true,
            noMultiply: true,
          }),
  },
  {
    label: 'Gross fee',
    key: 'grossFee',
    width: 150,
    render: (row) =>
      row.feeCalculationBasis === FeeCalculationBasis.Fixed
        ? numberToCurrencyString(row.grossFee, { currency })
        : numberToPercentageString(row.grossFee, {
            accuracy: 2,
            forceDecimals: true,
            noMultiply: true,
          }),
  },
  {
    label: 'Fee accounting',
    key: 'feeAccounting',
    width: 200,
    render: (row) =>
      ({
        [FeeAccounting.ChargedToInvestor]: 'Charged to investor',
        [FeeAccounting.Accrued]: 'Accrued',
      }[row.feeAccounting]),
  },
];

const performanceFeeColumns: Array<TableColumn<PerformanceFeeRow>> = [
  { label: 'Fee name', key: 'feeName', width: 200 },
  {
    label: 'Hurdle basis',
    key: 'hurdleBasis',
    width: 130,
    render: (row) =>
      row.hurdleBasis === FeeHurdleBasis.Company ? 'Company' : 'Subscription',
  },
  {
    label: 'Fee treatment',
    key: 'includeFeesInHurdle',
    width: 220,
    render: (row) =>
      row.includeFeesInHurdle
        ? 'Subscription including fees'
        : 'Subscription minus fees',
  },
  {
    label: 'Hurdle',
    key: 'hurdle',
    tooltip: '(1.0 = 1.0x return)',
    render: (row) => renderMultiple(row.hurdle),
    width: 120,
  },
  {
    label: 'Fee (ex VAT)',
    key: 'feePercentageExVAT',
    width: 130,
    render: (row) =>
      numberToPercentageString(row.feePercentageExVAT, {
        accuracy: 2,
        forceDecimals: true,
        noMultiply: true,
      }),
  },
  {
    label: 'VAT',
    key: 'vatPercentage',
    width: 100,
    render: (row) =>
      row.vatPercentage === 0
        ? 'No VAT'
        : numberToPercentageString(row.vatPercentage, {
            accuracy: 2,
            forceDecimals: true,
            noMultiply: true,
          }),
  },
  {
    label: 'Gross fee',
    key: 'feePercentageIncVAT',
    width: 150,
    render: (row) =>
      numberToPercentageString(row.feePercentageIncVAT, {
        accuracy: 2,
        forceDecimals: true,
        noMultiply: true,
      }),
  },
];

type Props = {
  control: Control<TrancheFormInput>;
  firmId?: string;
  currency: CurrencyCode;
};

const FeeManagement: FC<Props> = ({ control, firmId, currency }) => {
  const coreFeeColumns = buildCoreFeeColumns(currency);

  const coreFeeValue = useWatch({ control, name: 'defaultCoreFee' });
  const performanceFeeValue = useWatch({
    control,
    name: 'defaultPerformanceFee',
  });

  const { data: coreFeeTemplates } = api.coreFeeTemplates.list.useQuery(
    { orderBy: 'createdAt', order: SortOrder.Desc, firmId },
    { refetchOnWindowFocus: true },
  );

  const { data: performanceFeeTemplates } =
    api.performanceFeeTemplates.list.useQuery(
      { orderBy: 'createdAt', order: SortOrder.Desc, firmId },
      { refetchOnWindowFocus: true },
    );

  const { data: coreFeeTemplate, isLoading: isLoadingCoreFeeTemplate } =
    api.coreFeeTemplates.getById.useQuery(
      { id: coreFeeValue },
      { enabled: !!coreFeeValue },
    );

  const {
    data: performanceFeeTemplate,
    isLoading: isLoadingPerformanceFeeTemplate,
  } = api.performanceFeeTemplates.getById.useQuery(
    { id: performanceFeeValue },
    { enabled: !!performanceFeeValue },
  );

  const coreFeeOptions = [
    { value: '', label: 'No fees' },
    ...(coreFeeTemplates?.result ?? []).map((t) => ({
      value: t._id,
      label: t.name,
    })),
  ];

  const performanceFeeOptions = [
    { value: '', label: 'No fees' },
    ...(performanceFeeTemplates?.result ?? []).map((t) => ({
      value: t._id,
      label: t.name,
    })),
  ];

  const coreFeeRows: Array<CoreFeeRow> =
    coreFeeTemplate?.coreFees.map((fee) => ({ ...fee, id: fee._id })) ?? [];

  const performanceFeeRows: Array<PerformanceFeeRow> =
    performanceFeeTemplate?.performanceFees.map((fee, i) => ({
      ...fee,
      id: String(i),
    })) ?? [];

  return (
    <Column spacing="lg" fullWidth>
      <FormField
        extraInformation={
          <Typography variant="inherit" component="span">
            Select a default template for initial and recurring fees. You can
            override this on any investor's subscription page. Haven't created
            one yet?{' '}
            <a
              href={CORE_FEE_TEMPLATE_ADD}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TypographyButton text="Create template" />
            </a>
          </Typography>
        }
        spacing="sm"
        label="Select default core fee"
        size="lg"
      >
        <Controller
          control={control}
          name="defaultCoreFee"
          render={({ field }) => (
            <BasicSelect
              fullWidth
              size="small"
              placeholder="Select fee template"
              value={field.value}
              onChange={field.onChange}
              options={coreFeeOptions}
            />
          )}
        />
      </FormField>
      {coreFeeValue && (
        <Table
          scrollable
          columns={coreFeeColumns}
          rows={coreFeeRows}
          isLoading={isLoadingCoreFeeTemplate}
        />
      )}
      <FormField
        extraInformation={
          <Typography variant="inherit" component="span">
            Select a default template for performance fees. You can override
            this on any investor's subscription page. Haven't created one yet?{' '}
            <a
              href={PERFORMANCE_FEE_TEMPLATE_ADD}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TypographyButton text="Create template" />
            </a>
          </Typography>
        }
        spacing="sm"
        label="Select default performance fee"
        size="lg"
      >
        <Controller
          control={control}
          name="defaultPerformanceFee"
          render={({ field }) => (
            <BasicSelect
              fullWidth
              size="small"
              placeholder="Select fee template"
              value={field.value}
              onChange={field.onChange}
              options={performanceFeeOptions}
            />
          )}
        />
      </FormField>
      {performanceFeeValue && (
        <Table
          scrollable
          columns={performanceFeeColumns}
          rows={performanceFeeRows}
          isLoading={isLoadingPerformanceFeeTemplate}
        />
      )}
      <AdHocTooltipText
        variant="body2"
        tooltip={
          <>
            <p>Ad-hoc adjustments to this can be made in the following ways:</p>
            <br />
            <p>
              <strong>Discounts for advised investors:</strong> If all fees via
              a certain adviser have an initial fee discount or commission
              within the initial fee, this should be set on the adviser page
              before subscription applications are made by the adviser.
            </p>
            <br />
            <p>
              <strong>One-off fees/discounts:</strong> If there is an ad hoc
              adjustment to be made to the fee(s), this can be processed through
              the <i>One-off Fees / Discounts</i> function.
            </p>
          </>
        }
      >
        <Row spacing="xxs" centered>
          Making ad hoc and advised client fee adjustments
          <InfoOutline fontSize="inherit" />
        </Row>
      </AdHocTooltipText>
    </Column>
  );
};

export default FeeManagement;
